import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import MobileTailwind from "./MobileTailwind";
import HomeHeader from "./components/HomeHeader";
import BottomNav from "./components/BottomNav";
import DishCard, { Dish } from "./components/DishCard";
import EditDishModal from "./components/EditDishModal";
import AddonsPopup from "./components/AddonsPopup";
import CartStrip from "./components/CartStrip";
import { useCart } from "./CartContext";
import WebService from "../Services/WebService";
import { getEncryptedSessionData, setEncryptedSessionData } from "../Services/SessionStorageService";
import MobileLoader from "../components/MobileLoader/MobileLoader";
import SubHeader from "./components/SubHeader";

const keyFor = (d: Dish) => `${d.id}-${d.code}`;

const mapApiItemToDish = (item: any): Dish => {
  let type: "veg" | "nonveg" = "veg";
  if (item.food_type) {
    type = String(item.food_type).toLowerCase().includes("non") ? "nonveg" : "veg";
  } else if (item.is_veg !== undefined) {
    type = item.is_veg ? "veg" : "nonveg";
  } else if (item.type) {
    type = String(item.type).toLowerCase().includes("non") ? "nonveg" : "veg";
  } else if (item.choice) {
    type = String(item.choice).toLowerCase().includes("non") ? "nonveg" : "veg";
  }

  const availableQty = Number(item.available_quantity ?? -1);
  const dailyStock = Number(item.daily_stock ?? 0);
  // is_special = 1 ho, available_quantity = 0 ho, daily_stock > 0 ho — tab Out of Stock
  const isSpecial = item.is_special === 1 || item.is_special === true || item.is_special === "1";
  const isOutOfStock = isSpecial && availableQty === 0 && dailyStock > 0;

  return {
    id: String(item.id),
    name: item.name || item.item_name || item.menu_name || "",
    code: item.custom_code ?? item.code ?? item.item_code ?? 0,
    price: Number(item.price ?? item.gross_price ?? 0),
    gross_price: Number(item.gross_price ?? item.price ?? 0),
    type,
    isOutOfStock,
  };
};

const Dishes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { categoryKey = "", tableNo } = useParams<{ categoryKey: string; tableNo: string }>();
  const cart = useCart();
  const [search, setSearch] = useState("");
  const [editingDish, setEditingDish] = useState<Dish | null>(null);
  const [addonDish, setAddonDish] = useState<Dish | null>(null);
  const [dishes, setDishes] = useState<Dish[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const stateCategoryName = location.state?.categoryName;

  const categoryName = useMemo(() => {
    if (stateCategoryName) return stateCategoryName;
    if (categoryKey === "special") return "Special";
    const tenantId = localStorage.getItem("tenantId") || "default";
    const cacheKey = `menu_categories_swr_${tenantId}`;
    const cachedCategories = getEncryptedSessionData<any[]>(cacheKey);
    const found = cachedCategories?.find((c) => String(c.id) === String(categoryKey));
    return found?.name || "";
  }, [categoryKey, stateCategoryName]);

  useEffect(() => {
    if (tableNo && cart.tableNo !== tableNo) cart.setTable(tableNo);
  }, [tableNo, cart]);

  // ── SWR (Stale-While-Revalidate) category items load ──
  const getMenuItems = useCallback(() => {
    const tenantId = localStorage.getItem('tenantId') || 'default';
    const cacheKey = `dishes_swr_${tenantId}_${categoryKey}`;
    const isSpecial = categoryKey === "special";
    const cached = isSpecial ? null : getEncryptedSessionData<Dish[]>(cacheKey);
    if (cached) {
      setDishes(cached);
    } else {
      setIsLoading(true);
    }

    const apiAction = isSpecial ? "/get-all/special-menu-items" : "/get-all/menu-items";
    const apiBody = isSpecial ? {} : { server_category_id: categoryKey };
    WebService.getAPI({ action: apiAction, body: apiBody })
      .then((response: any) => {
        const raw: any[] = response?.menus || response?.items || [];
        let mapped = raw.map(mapApiItemToDish);
        if (isSpecial) {
          mapped = mapped.sort((a, b) => {
            if (a.isOutOfStock && !b.isOutOfStock) return 1;
            if (!a.isOutOfStock && b.isOutOfStock) return -1;
            return 0;
          });
        }
        setDishes(mapped);
        if (!isSpecial) {
          setEncryptedSessionData(cacheKey, mapped);
        }
      })
      .catch((err) => console.error("MenuItems swr fetch error:", err))
      .finally(() => setIsLoading(false));
  }, [categoryKey]);

  useEffect(() => {
    if (categoryKey) getMenuItems();
  }, [categoryKey, getMenuItems]);

  // ── Search live data se — sessionStorage nahi ──
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return dishes;
    if (/^\d+$/.test(q)) {
      return dishes.filter(
        (d) => String(d.code).trim().toLowerCase() === q
      );
    }
    return dishes.filter(
      (d) =>
        d.name.toLowerCase().includes(q) ||
        String(d.code).trim().toLowerCase() === q
    );
  }, [dishes, search]);
  const qtyOf = (d: Dish) => cart.lines.find((l) => l.key === keyFor(d))?.quantity ?? 0;

  return (
    <>
      <MobileTailwind />
      <div id="mobile-root" className="h-screen w-full bg-black flex justify-center overflow-hidden">
        <div className="relative w-full h-screen overflow-y-auto overflow-x-hidden" style={{ backgroundColor: "#F4F4F5" }} >
          <SubHeader title={categoryName ? (<> Dishes{" "}  <span className="text-[14px] font-normal text-[black]">({categoryName}) </span> </>) : ("Dishes")}
          />
          <HomeHeader searchValue={search} onSearchChange={setSearch} />
          <MobileLoader show={isLoading} />
          <div className="px-3 md:px-6 pt-4 pb-[120px] flex flex-col gap-3">
            {!isLoading && filtered.length === 0 && (
              <p className="text-center font-opensans text-[15px] text-[#8F9098] mt-6">
                No dishes found
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {filtered.map((d) => {
                const k = keyFor(d);
                return (
                  <DishCard
                    key={k}
                    dish={d}
                    quantity={qtyOf(d)}
                    onAdd={() => !d.isOutOfStock && setEditingDish(d)}
                    onIncrement={() => cart.increment(k)}
                    onDecrement={() => cart.decrement(k)}
                    onEdit={() => !d.isOutOfStock && setEditingDish(d)}
                  />
                );
              })}
            </div>
          </div>

          {cart.totalItems > 0 ? (
            <CartStrip
              tableNo={cart.tableNo ?? tableNo}
              itemCount={cart.totalItems}
              onViewCart={() => navigate("/mobile/cart")}
              onClose={() => cart.clear()}
            />
          ) : (
            <BottomNav active="menu" tableNo={tableNo} />
          )}
        </div>

        <AddonsPopup
          open={addonDish !== null}
          dish={addonDish}
          onClose={() => setAddonDish(null)}
          onAddToCart={({ dish }) => {
            cart.addItem({ key: keyFor(dish), dish, quantity: 1 });
            setAddonDish(null);
          }}
        />

        <EditDishModal
          open={editingDish !== null}
          dish={editingDish}
          initialQuantity={editingDish ? qtyOf(editingDish) || 1 : 1}
          initialNote={editingDish ? cart.lines.find((l) => l.key === keyFor(editingDish))?.note ?? "" : ""}
          onClose={() => setEditingDish(null)}
          onAddToCart={({ dish, quantity, note }) => {
            const k = keyFor(dish);
            const existing = cart.lines.find((l) => l.key === k);
            if (existing) {
              cart.setQuantity(k, quantity);
            } else {
              cart.addItem({ key: k, dish, quantity, note });
            }
            setEditingDish(null);
          }}
        />
      </div>
    </>
  );
};

export default Dishes;